import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'school');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = getCategoryBySlug(activeCategory);
  const comicsList = getComicsByCategory(activeCategory);

  const sortedComics = [...comicsList].sort((a, b) => {
    if (sortBy === 'popular') return b.views - a.views;
    if (sortBy === 'rating') return b.rating - a.rating;
    return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
  });

  const categoryTitle = currentCategory ? `${currentCategory.name}漫画` : '漫画分类';
  const categoryDesc = currentCategory
    ? `韩国漫画网${currentCategory.name}漫画专区，收录${currentCategory.count}部精选${currentCategory.name}题材韩漫作品。免费在线观看最新${currentCategory.name}韩国漫画，每日更新。`
    : '';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryTitle} - 韩国漫画网`,
    description: categoryDesc,
    url: `https://www.koreancomic.cn/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'https://www.koreancomic.cn/',
    },
  };

  return (
    <>
      <SEOHead
        title={`${categoryTitle} - 韩国漫画网 | 免费韩漫在线看`}
        description={categoryDesc}
        keywords={`韩漫,免费韩漫,${categoryTitle},韩国漫画,在线漫画`}
        canonicalUrl={`https://www.koreancomic.cn/category/${activeCategory}`}
        structuredData={structuredData}
      />

      <Navbar />

      <main className="pt-24 pb-10 md:pb-14">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryTitle },
              ]}
            />

            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {categoryTitle}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                {categoryDesc}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-900">{comicsList.length}</strong> 部作品
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                {([
                  { key: 'latest', label: '最新' },
                  { key: 'popular', label: '最热' },
                  { key: 'rating', label: '评分' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {sortedComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="">
                {sortedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                  <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂时没有漫画，请浏览其他分类</p>
                <Link
                  to="/"
                  className="inline-block mt-4 text-primary-500 hover:text-primary-600 text-sm font-medium"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}